import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { MutationBaseOptions } from '@apollo/client/core/watchQueryOptions';

import { aftermarketPartnerApi } from './index';
import { aftermarketProductsApi } from '../../products/api';
import {
  CreatePartnerProfileDTO,
  UpsertPartnerProfileDTO,
  UpsertPartnerProfileResponse,
  CreatePartnerProfileGraphQLInput,
  UpsertPartnerProfileGraphQLInput,
} from '../types';

export function usePartner() {
  const { showNotification } = useNotifications();

  const [onCreatePartnerAccount, { loading: isPartnerAccountCreating }] = useMutation<
    any,
    CreatePartnerProfileGraphQLInput
  >(aftermarketPartnerApi.createPartnerAccount, { onError: logError });

  const [onUpsertPartnerProfile, { loading: isPartnerProfileUpdating }] = useMutation<
    UpsertPartnerProfileResponse,
    UpsertPartnerProfileGraphQLInput
  >(aftermarketPartnerApi.upsertPartnerProfile, { onError: logError });

  const handleCreatePartnerAccount = useCallback(
    async (
      createDTO: CreatePartnerProfileDTO,
      fetchConfig?: (api: {
        partnerApi: typeof aftermarketPartnerApi;
        productsApi: typeof aftermarketProductsApi;
      }) => Omit<MutationBaseOptions<any, CreatePartnerProfileGraphQLInput>, 'variables'>
    ) => {
      try {
        const { data } = await onCreatePartnerAccount({
          variables: { input: createDTO },
          ...(typeof fetchConfig === 'function'
            ? fetchConfig({ partnerApi: aftermarketPartnerApi, productsApi: aftermarketProductsApi })
            : {}),
        });

        if (!data?.result?.success || data?.result?.error || !data?.result?.entity) {
          showNotification('Something went wrong while creating partner account', { type: 'error' });
          logError(data?.result?.error);

          return;
        }

        showNotification(`Partner ${createDTO.contact.title} created!`);

        return data.result.entity;
      } catch (error) {
        showNotification('Something went wrong while creating partner account', { type: 'error' });
      }
    },
    [onCreatePartnerAccount, showNotification]
  );

  const handleUpdatePartnerProfile = useCallback(
    async (
      updateDTO: UpsertPartnerProfileDTO,
      fetchConfig?: (api: {
        partnerApi: typeof aftermarketPartnerApi;
        productsApi: typeof aftermarketProductsApi;
      }) => Omit<MutationBaseOptions<UpsertPartnerProfileResponse, UpsertPartnerProfileGraphQLInput>, 'variables'>
    ) => {
      try {
        const { data } = await onUpsertPartnerProfile({
          variables: { input: updateDTO },
          ...(typeof fetchConfig === 'function'
            ? fetchConfig({ partnerApi: aftermarketPartnerApi, productsApi: aftermarketProductsApi })
            : {}),
        });

        if (!data?.result?.id) {
          showNotification('Something went wrong while updating partner profile', { type: 'error' });

          return;
        }

        showNotification('Partner profile updated successfully!');

        return data.result.id;
      } catch (error) {
        showNotification('Something went wrong while updating partner profile', { type: 'error' });
      }
    },
    [onUpsertPartnerProfile, showNotification]
  );

  return {
    isPartnerAccountCreating,
    isPartnerProfileUpdating,
    handleCreatePartnerAccount,
    handleUpdatePartnerProfile,
  };
}
